import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Phone, Mail, MessageSquareText, ArrowUpRight, Clock, Landmark } from "lucide-react";
import { SITE } from "../data/site";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { SectionHead } from "../components/SectionHead";
import { PrimaryCTA } from "../components/CTA";
import { OpenStatus } from "../components/OpenStatus";
import { TDBadge, TDInline } from "../components/TDBadge";
import { FloatingIcons } from "../components/FloatingIcons";
import { Parallax } from "../components/Motion";
import { DeviceDrift } from "../components/DeviceDrift";
import { useLang } from "../i18n";
import { Smartphone, Wrench } from "lucide-react";

export default function Visit() {
  const { t } = useLang();
  const reduce = useReducedMotion();
  const ACTIONS = [
    { icon: Phone, label: `${t("visit.call")} ${SITE.phoneDisplay}`, href: SITE.phoneHref, testid: "visit-call-button", primary: true },
    { icon: MessageSquareText, label: t("visit.text"), href: SITE.smsHref, testid: "visit-text-button" },
    { icon: Mail, label: t("visit.email"), href: SITE.emailHref, testid: "visit-email-button" },
    { icon: MapPin, label: t("visit.openMaps"), href: SITE.mapsUrl, testid: "visit-map-button", external: true },
  ];
  return (
    <>
      <section data-testid="visit-hero" className="grain relative overflow-hidden pt-28 sm:pt-36 lg:pt-44 pb-12 sm:pb-16">
        <div className="pointer-events-none absolute -top-24 -left-32 h-[26rem] w-[26rem] rounded-full bg-accent/15 blur-3xl" aria-hidden="true" />
        <FloatingIcons
          items={[
            { Icon: Smartphone, x: "86%", y: "18%", dur: 10 },
            { Icon: Wrench, x: "8%", y: "62%", dur: 12, delay: 0.8 },
          ]}
        />
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <SectionHead
            chapter={t("visit.chapter")}
            eyebrow={t("visit.eyebrow")}
            title={<>{t("visit.titleA")} <span className="italic text-accent-bright">{t("visit.titleEm")}</span> <TDInline /></>}
            lede={t("visit.lede")}
          />
          <Reveal delay={0.15}>
            <div className="mt-8">
              <OpenStatus />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 sm:pb-32 lg:px-16">
        <Reveal className="mb-12">
          <div
            data-testid="visit-finder-strip"
            className="glass-liquid grain relative flex flex-col items-center justify-center gap-6 overflow-hidden rounded-[28px] px-6 py-12 sm:flex-row sm:gap-14 sm:py-14"
          >
            <div className="pointer-events-none absolute -top-20 left-1/4 h-56 w-56 rounded-full bg-gold/15 blur-3xl" aria-hidden="true" />
            <motion.img
              src={SITE.logo}
              alt="Next-G Phone Repair logo"
              data-testid="visit-floating-logo"
              animate={reduce ? {} : { y: [0, -14, 0], rotate: [3, -3, 3] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
              className="h-28 w-28 rounded-full object-cover ring-1 ring-gold/50 sm:h-36 sm:w-36"
              style={{ filter: "drop-shadow(0 0 38px rgba(201,155,63,0.55))" }}
            />
            <motion.span
              aria-hidden="true"
              className="hidden h-px w-24 bg-gradient-to-r from-gold/70 via-accent/70 to-[#34A233]/70 sm:block"
              animate={reduce ? {} : { opacity: [0.3, 1, 0.3], scaleX: [0.7, 1, 0.7] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <div className="text-center sm:text-start">
              <p className="font-display text-2xl sm:text-3xl font-medium tracking-tight">
                {SITE.addressShort}
              </p>
              <p className="mt-1 flex items-center justify-center gap-2 text-sm text-ink/60 sm:justify-start">
                <Landmark className="h-4 w-4 text-[#4CC35B]" /> {SITE.locationCue}
              </p>
            </div>
            <motion.span
              aria-hidden="true"
              className="hidden h-px w-24 bg-gradient-to-r from-[#34A233]/70 via-accent/70 to-gold/70 sm:block"
              animate={reduce ? {} : { opacity: [1, 0.3, 1], scaleX: [1, 0.7, 1] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            />
            <Parallax speed={0.35}><TDBadge /></Parallax>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="space-y-10 lg:col-span-5">
            <Reveal>
              <div data-testid="visit-address-card" className="glass-liquid rounded-[24px] p-7 sm:p-9">
                <p className="eyebrow">{t("visit.addressLabel")}</p>
                <p className="mt-4 font-display text-2xl sm:text-3xl font-medium tracking-tight">
                  {SITE.address}
                </p>
                <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold/15 px-4 py-2 text-sm font-medium text-gold-soft">
                  <Landmark className="h-4 w-4" /> {SITE.locationCue}
                </p>
                <div className="mt-6 border-t border-sand pt-6">
                  <p className="eyebrow flex items-center gap-2"><Clock className="h-3.5 w-3.5" /> {t("visit.hoursLabel")}</p>
                  <ul className="mt-4 space-y-3">
                    {SITE.hours.map((h) => (
                      <li
                        key={h.days}
                        data-testid={`visit-hours-${h.days === "Sunday" ? "sunday" : "weekday"}`}
                        className="flex items-center justify-between text-sm sm:text-base"
                      >
                        <span className="text-ink/75">{h.days}</span>
                        <span className="font-mono text-xs sm:text-sm">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>

            <Stagger className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {ACTIONS.map((a) => {
                const Icon = a.icon;
                return (
                  <StaggerItem key={a.testid}>
                    <a
                      href={a.href}
                      data-testid={a.testid}
                      {...(a.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className={`group flex items-center justify-between gap-3 rounded-2xl px-6 py-5 text-sm sm:text-base font-semibold transition-colors duration-300 ${
                        a.primary
                          ? "bg-accent text-[#EDF1F7] hover:bg-accent-deep"
                          : "border border-ink/15 bg-white/5 hover:border-ink hover:bg-ink hover:text-paper"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Icon className="h-5 w-5" /> {a.label}
                      </span>
                      <ArrowUpRight className="h-4 w-4 opacity-60 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
                    </a>
                  </StaggerItem>
                );
              })}
            </Stagger>

            <Reveal>
              <div className="rounded-[24px] border border-ink/10 bg-espresso p-7 sm:p-9">
                <p className="eyebrow !text-gold-soft">{t("visit.planEyebrow")}</p>
                <p className="mt-3 font-display text-xl sm:text-2xl font-medium tracking-tight">
                  {t("visit.planTitle")}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <PrimaryCTA to="/quote" testid="visit-quote-cta" dark>{t("visit.planCta")}</PrimaryCTA>
                </div>
              </div>
            </Reveal>
          </div>

          <div className="relative lg:col-span-7">
            <DeviceDrift device="moon" className="-top-12 right-[4%] z-20 lg:-top-16 lg:right-[6%]" imgClassName="h-[54px] lg:h-[100px]" dur={17} opacity="opacity-70 lg:opacity-90" />
            <Reveal delay={0.1} className="h-full">
              <div
                data-testid="visit-map-frame"
                className="relative h-full min-h-[26rem] overflow-hidden rounded-[24px] border border-sand bg-cream shadow-[0_40px_80px_-40px_rgba(0,0,0,0.8)]"
              >
                <iframe
                  title={`${t("visit.mapTitle")}, ${SITE.address}`}
                  src={SITE.mapsEmbed}
                  className="absolute inset-0 h-full w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-10 h-full w-full"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <motion.path
                    data-testid="visit-route-line"
                    d="M 6 88 C 26 74, 34 60, 52 52 S 76 34, 84 22"
                    fill="none"
                    stroke="rgba(233,180,76,0.95)"
                    strokeWidth="0.9"
                    strokeDasharray="2.6 2.2"
                    strokeLinecap="round"
                    initial={reduce ? false : { pathLength: 0 }}
                    whileInView={{ pathLength: 1 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 2.4, ease: "easeInOut", delay: 0.5 }}
                  />
                </svg>
                <span
                  aria-hidden="true"
                  className="absolute h-2.5 w-2.5 rounded-full bg-cyan shadow-[0_0_10px_rgba(79,179,191,0.9)]"
                  style={{ left: "6%", top: "88%" }}
                />
                <motion.span
                  aria-hidden="true"
                  className="absolute z-10 flex h-6 w-6 items-center justify-center rounded-full bg-volt text-[#0B111B]"
                  style={{ left: "84%", top: "22%", translate: "-50% -50%" }}
                  initial={reduce ? false : { scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 2.7, type: "spring", stiffness: 300, damping: 15 }}
                >
                  <MapPin className="h-3.5 w-3.5" />
                  {!reduce && (
                    <span className="absolute inset-0 animate-ping rounded-full bg-volt/60 [animation-duration:1.8s]" />
                  )}
                </motion.span>
                <div className="pointer-events-none absolute start-4 top-4 rounded-full bg-espresso/90 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-soft backdrop-blur">
                  {SITE.addressShort} · {SITE.locationCue}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
